import { Component, computed, input } from "@angular/core";
import { RewardCursor } from "@domain/User";
import { RdxProgressIndicatorDirective, RdxProgressRootDirective } from "@radix-ng/primitives/progress";

@Component({
  selector: "app-reward-cursor-progress",
  imports: [RdxProgressIndicatorDirective, RdxProgressRootDirective],
  template: `
    <div
      [class]="{
        'progress-circle': true,
        'is-next': isNext(),
        'is-unavailable': isLocked(),
      }"
      [attr.data-testid]="'reward-cursors-progression-' + rewardCursor()"
      rdxProgressRoot
      [max]="maxStep"
      [value]="currentStep()"
      [valueLabel]="getValueLabel"
    >
      <svg>
        <circle></circle>
        <circle rdxProgressIndicator [attr.stroke-dashoffset]="strokeDashoffset()"></circle>
      </svg>
      <div class="progress-circle__content">
        <div class="content__image">
          <img [src]="'assets/cursors/' + rewardCursor() + '.svg'" [attr.alt]="cursorAltText()" />
        </div>
        <p>{{ currentStep() }} / {{ maxStep }}</p>
      </div>
    </div>
  `,
  styles: `
    @use "responsive" as *;

    .progress-circle {
      width: 100px;
      height: 100px;
      border-radius: 50%;
      overflow: hidden;
      position: relative;

      @include screenWidth("from-tablet") {
        height: 120px;
        width: 120px;
      }

      svg {
        height: 100px;
        width: 100px;

        @include screenWidth("from-tablet") {
          height: 120px;
          width: 120px;
        }
      }

      circle {
        stroke-width: 10;
        stroke-linecap: round;
        stroke-dasharray: 276;
        cx: 50px;
        cy: 50px;
        r: 44px;

        @include screenWidth("from-tablet") {
          cx: 60px;
          cy: 60px;
          r: 54px;
          stroke-dasharray: 339;
        }
      }

      circle:nth-child(1) {
        fill: #caedf9;
        stroke: #f5f3ef;
      }

      circle:nth-child(2) {
        fill: none;
        stroke: #0c8eb1;
        transform-box: fill-box;
        transform-origin: center;
        transform: rotate(-90deg);

        @include screenWidth("from-tablet") {
        }
      }

      &.is-unavailable {
        filter: grayscale(1);

        svg {
          opacity: 0.6;
        }

        .progress-circle__content {
          opacity: 0.4;
        }

        circle:nth-child(1) {
          fill: white;
        }

        circle:nth-child(2) {
          stroke: none;
        }
      }

      &.is-next {
        circle:nth-child(2) {
          stroke: #05bf8e;
        }
      }
    }

    .progress-circle__content {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      gap: 2px;
      padding-bottom: 15px;
      padding-top: 15px;

      .content__image {
        flex-shrink: 0;
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;

        > img {
          max-height: 45px;
          max-width: 60px;

          @include screenWidth("from-tablet") {
            max-height: 57px;
            max-width: 75px;
          }
        }
      }

      > p {
        flex-grow: 0;
        flex-shrink: 0;
        font-size: 1.4rem;
        font-weight: 700;
      }
    }
  `,
})
export class RewardCursorProgress {
  maxStep = 24;

  rewardCursor = input.required<RewardCursor>();
  currentStep = input.required<number>();
  isNext = input(false);
  isLocked = input(false);

  progressionPercentage = computed(() => {
    const currentStep = this.currentStep();
    if (currentStep === 0) return 0;

    if (currentStep > this.maxStep) return 100;

    return (100 * currentStep) / this.maxStep;
  });

  cursorAltText = computed(() => {
    if (this.isNext()) {
      const stepsBeforeIsUnlocked = this.maxStep - this.currentStep();

      if (stepsBeforeIsUnlocked > 1) {
        return `Le curseur ${this.rewardCursor()} sera débloqué dans ${stepsBeforeIsUnlocked} bonnes réponses`;
      } else {
        return `Le curseur ${this.rewardCursor()} sera débloqué dans 1 bonne réponse`;
      }
    }

    if (this.isLocked()) {
      return `Le curseur ${this.rewardCursor()} n'est toujours pas disponible`;
    }

    return `Le curseur ${this.rewardCursor()} est disponible`;
  });

  getValueLabel = (value: number, max: number) => {
    if (value < 2) {
      return `${value} bonne réponse obtenue sur ${max}`;
    }

    return `${value} bonnes réponses obtenues sur ${max}`;
  };

  // Calculated directly to avoid Firefox issues with calc() in stroke-dashoffset
  strokeDashoffset = computed(() => {
    const circumference = window.innerWidth >= 768 ? 339 : 276;
    return circumference - (circumference * this.progressionPercentage()) / 100;
  });
}
